import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, FormControlLabel, Switch, 
  TextField, Button, CircularProgress, Divider 
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import api from '../../utils/axios';
import { toast } from 'react-toastify';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    disableNewRegistrations: false,
    forceAppUpdateVersion: '',
    announcementBanner: {
      text: '',
      isActive: false,
      link: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/superadmin/settings');
      if (res.data.success) {
        setSettings(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/superadmin/settings', settings);
      if (res.data.success) {
        toast.success('Settings updated successfully');
        setSettings(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">System Settings & Feature Flags</Typography>
          <Typography variant="body1" color="text.secondary">
            Global configuration and feature toggles.
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save Settings
        </Button>
      </Box>

      <Card sx={{ maxWidth: 800, mb: 4 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 4 }}>
          
          <Typography variant="h6">General Toggles</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={settings.maintenanceMode} onChange={(e) => setSettings({...settings, maintenanceMode: e.target.checked})} color="error" />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>Maintenance Mode</Typography>
                  <Typography variant="body2" color="text.secondary">When active, the app will display a maintenance screen to non-admin users.</Typography>
                </Box>
              }
            />
            <Divider />
            <FormControlLabel
              control={<Switch checked={settings.disableNewRegistrations} onChange={(e) => setSettings({...settings, disableNewRegistrations: e.target.checked})} color="warning" />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>Disable New Registrations</Typography>
                  <Typography variant="body2" color="text.secondary">Prevents any new user from creating an account.</Typography>
                </Box>
              }
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6">App Versioning</Typography>
          <TextField 
            label="Force Update to Version" 
            variant="outlined" 
            fullWidth 
            value={settings.forceAppUpdateVersion}
            onChange={(e) => setSettings({...settings, forceAppUpdateVersion: e.target.value})}
            placeholder="e.g., 2.1.0"
            helperText="If specified, users on older versions of the mobile app will be forced to update."
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6">Global Announcement Banner</Typography>
          <FormControlLabel
            control={<Switch checked={settings.announcementBanner.isActive} onChange={(e) => setSettings({...settings, announcementBanner: {...settings.announcementBanner, isActive: e.target.checked}})} color="primary" />}
            label="Enable Global Banner"
          />
          <TextField 
            label="Banner Text" 
            variant="outlined" 
            fullWidth 
            disabled={!settings.announcementBanner.isActive}
            value={settings.announcementBanner.text}
            onChange={(e) => setSettings({...settings, announcementBanner: {...settings.announcementBanner, text: e.target.value}})}
          />
          <TextField 
            label="Banner Action Link (Optional)" 
            variant="outlined" 
            fullWidth 
            disabled={!settings.announcementBanner.isActive}
            value={settings.announcementBanner.link}
            onChange={(e) => setSettings({...settings, announcementBanner: {...settings.announcementBanner, link: e.target.value}})}
            placeholder="https://"
          />

        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemSettings;
